import { useCallback, useEffect, useRef, useState } from 'react'

import { Skeleton } from '@mui/material'
import { type HeadItemType, Pagination, Table } from 'components'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { getOVTracking } from 'services/manage_ov'
import type { OvTracking } from 'services/manage_ov/interface'

import * as S from '../styles'

const ModalTrackingOV = () => {
  const { ovNumber } = useParams()
  const [loading, setLoading] = useState(true)
  const [trackings, setTrackings] = useState<OvTracking[]>([])

  const [currentPage, setCurrentPage] = useState<number>(1)
  const [totalPages, setTotalPages] = useState<number>(0)
  const [totalItems, setTotalItems] = useState<number>(0)
  const PAGE_SIZE = 10

  const isMounted = useRef(true)

  const headValues: HeadItemType[] = [
    { columnName: 'Data', label: 'Data' },
    { columnName: 'Hora', label: 'Hora' },
    { columnName: 'Usuario', label: 'Usuário' },
    { columnName: 'Descricao', label: 'Descrição' },
    { columnName: 'DescricaoComplementar', label: 'Descrição Complementar' },
  ]

  const renderSkeleton = useCallback(() => {
    const skeleton = []
    for (let i = 0; i < 3; i++) {
      skeleton.push({
        content: [
          <Skeleton height={30} key={`tracking-skeleton-1-${i}`} width={130} />,
          <Skeleton height={30} key={`tracking-skeleton-2-${i}`} width={80} />,
          <Skeleton height={30} key={`tracking-skeleton-3-${i}`} width={100} />,
          <Skeleton height={30} key={`tracking-skeleton-4-${i}`} width={180} />,
          <Skeleton height={30} key={`tracking-skeleton-5-${i}`} width={300} />,
        ],
        id: `tracking-loading-${i}`,
      })
    }
    return skeleton
  }, [])

  const renderBody = useCallback(() => {
    if (loading) return renderSkeleton()
    if (!trackings.length) return []

    return trackings.map((tracking, index) => {
      return {
        content: [
          tracking.DataCriacao ?? '--',
          tracking.HoraCriacao ?? '--',
          tracking.UsuarioCadastro ?? '--',
          tracking.DescricaoOcorrenciaOrdemVenda ?? '--',
          tracking.DescricaoComplementarOrdemVenda ?? '--',
        ],
        id: `tracking-item-${index}`,
      }
    })
  }, [loading, trackings, renderSkeleton])

  useEffect(() => {
    isMounted.current = true

    const getTracking = async () => {
      setLoading(true)
      try {
        const response = await getOVTracking(
          { Linhas: PAGE_SIZE, Paginas: currentPage - 1 },
          `${ovNumber}`,
        )
        if (isMounted.current) {
          const rowsCount = response[0].RowsCount ?? 0
          setTrackings(response)
          setTotalItems(rowsCount)
          setTotalPages(Math.ceil(rowsCount / PAGE_SIZE))
        }
      } catch (error) {
        if (isMounted.current) toast.error('Erro ao carregar rastreamento')
      } finally {
        if (isMounted.current) setLoading(false)
      }
    }

    if (ovNumber) getTracking()

    return () => {
      isMounted.current = false
    }
  }, [currentPage, ovNumber])

  return (
    <>
      <S.TableWrapper>
        <Table bodyData={renderBody()} headData={headValues} notScrollable rowHeight={40} />
      </S.TableWrapper>

      {trackings.length > 0 && (
        <Pagination
          currentPage={currentPage}
          disabled={loading}
          itemsPerPage={PAGE_SIZE}
          numberOfItems={totalItems}
          numberOfPages={totalPages}
          setCurrentPage={setCurrentPage}
        />
      )}
    </>
  )
}

export default ModalTrackingOV
